import { t } from "i18next";
import React, { useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { LoaderContext } from "../../AuthContext/AuthContext";
import { useNavigate } from "react-router-dom";

const PrivacyPolicyPage = () => {
  const { selectedLanguageID } = useContext(LoaderContext);
  const navigate = useNavigate()
  useEffect(() => {
    navigate('/privacy-policy')
  }, [selectedLanguageID])
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Myomnamo - Spiritual Services Platform</title>
        <meta
          name="description"
          content="Read our Privacy Policy to understand how we protect your data. Learn about information collection, usage, and your rights as a user."
        />

        {/* Canonical Tag */}
        <link rel="canonical" href="https://www.Myomnamo.com/privacy-policy" />

        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Privacy Policy - Myomnamo - Spiritual Services Platform"
        />
        <meta
          property="og:description"
          content="Read our Privacy Policy to understand how we protect your data. Learn about information collection, usage, and your rights as a user."
        />
        <meta
          property="og:image"
          content="https://www.Myomnamo.com/assets/images/new/logo.png"
        />
        <meta
          property="og:url"
          content="https://www.Myomnamo.com/privacy-policy"
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Myomnamo" />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Privacy Policy - Myomnamo - Spiritual Services Platform"
        />
        <meta
          name="twitter:description"
          content="Read our Privacy Policy to understand how we protect your data. Learn about information collection, usage, and your rights as a user."
        />
        <meta
          name="twitter:image"
          content="https://www.Myomnamo.com/assets/images/new/logo.png"
        />

        {/* Schema Markup - Breadcrumb List */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.Myomnamo.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Privacy Policy",
                item: "https://www.Myomnamo.com/privacy-policy",
              },
            ],
          })}
        </script>

        {/* Schema Markup - WebPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "WebPage",
            name: "Privacy Policy - Myomnamo - Spiritual Services Platform",
            url: "https://www.Myomnamo.com/privacy-policy",
            description:
              "Read our Privacy Policy to understand how we protect your data. Learn about information collection, usage, and your rights as a user.",
            publisher: {
              "@type": "Organization",
              name: "Myomnamo",
            },
          })}
        </script>
      </Helmet>
      <section>
        <div
          className="container mx-auto p-6 mt-5"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <h3 className="text-2xl font-bold mb-4">
            {t("Privacy Policy for Myomnamo")}
          </h3>
          <p>
            {t("Welcome to Myomnamo. Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your personal information when you use our website")} (
            <a href="https://www.Myomnamo.com" className="text-blue-500">
              www.myomnamo.com
            </a>
            ) {t("and our spiritual services.")}
          </p>

          <h4 className="text-xl font-semibold mt-6">
            {t("1. Information We Collect")}
          </h4>
          <p>
            {t("We collect personal and non-personal information when you use our website and services.")}
          </p>

          <h5 className="font-medium mt-4">{t("Personal Information:")}</h5>
          <ul className="list-disc pl-6">
            <li>{t("Name")}</li>
            <li>{t("Contact details (email, phone number, address)")}</li>
            <li>{t("Payment details (processed through secure gateways)")}</li>
            <li>{t("Date of birth (for horoscope services)")}</li>
            <li>{t("Preferences and requirements for bookings")}</li>
          </ul>

          <h5 className="font-medium mt-4">{t("Non-Personal Information:")}</h5>
          <ul className="list-disc pl-6">
            <li>{t("Browser type and version")}</li>
            <li>{t("Device and operating system")}</li>
            <li>{t("IP address")}</li>
            <li>{t("Pages visited and time spent on the website")}</li>
            <li>{t("Cookies and tracking data")}</li>
          </ul>

          <h4 className="text-xl font-semibold mt-6">
            {t("2. How We Use Your Information")}
          </h4>
          <ul className="list-disc pl-6">
            <li>{t("Processing puja, darshan, and horoscope bookings")}</li>
            <li>{t("Delivering e-store purchases")}</li>
            <li>{t("Personalizing recommendations based on your preferences")}</li>
            <li>{t("Sending booking confirmations, reminders, and updates")}</li>
            <li>{t("Responding to inquiries and providing customer support")}</li>
            <li>{t("Improving website functionality and user experience")}</li>
            <li>{t("Complying with legal and regulatory requirements")}</li>
          </ul>

          <h4 className="text-xl font-semibold mt-6">
            {t("3. How We Share Your Information")}
          </h4>
          <ul className="list-disc pl-6">
            <li>
              <strong>{t("Service Providers:")}</strong> {t("We share necessary details with pandits, astrologers, and logistics providers to fulfill your bookings and orders.")}
            </li>
            <li>
              <strong>{t("Payment Processors:")}</strong> {t("Your payment information is securely processed by third-party payment gateways.")}
            </li>
            <li>
              <strong>{t("Legal Compliance:")}</strong> {t("If required by law, we may disclose your information.")}
            </li>
            <li>
              <strong>{t("Business Transfers:")}</strong> {t("In case of a merger or sale, your data may be shared under the same privacy standards.")}
            </li>
          </ul>

          <h4 className="text-xl font-semibold mt-6">{t("4. Data Security")}</h4>
          <p>
            {t("We take appropriate security measures to protect your personal information:")}
          </p>
          <ul className="list-disc pl-6">
            <li>{t("Secure payment gateways with encryption")}</li>
            <li>{t("Access controls and authentication for data protection")}</li>
            <li>{t("Regular monitoring of our systems for vulnerabilities")}</li>
          </ul>
          <p>
            {t("However, no method of transmission over the internet is 100% secure.")}
          </p>

          <h4 className="text-xl font-semibold mt-6">
            {t("5. Cookies and Tracking Technologies")}
          </h4>
          <p>
            {t("We use cookies and tracking technologies to improve user experience:")}
          </p>
          <ul className="list-disc pl-6">
            <li>{t("Recognizing returning users")}</li>
            <li>{t("Storing user preferences")}</li>
            <li>{t("Analyzing website traffic")}</li>
          </ul>
          <p>
            {t("You can disable cookies through your browser settings, but this may affect functionality.")}
          </p>

          <h4 className="text-xl font-semibold mt-6">
            {t("6. Your Rights and Choices")}
          </h4>
          <p>{t("You have the right to:")}</p>
          <ul className="list-disc pl-6">
            <li>
              <strong>{t("Access Your Data:")}</strong> {t("Request a copy of your personal information.")}
            </li>
            <li>
              <strong>{t("Update or Correct Data:")}</strong> {t("Modify incorrect or outdated information.")}
            </li>
            <li>
              <strong>{t("Opt-Out of Marketing:")}</strong> {t("Unsubscribe from promotional emails.")}
            </li>
            <li>
              <strong>{t("Request Data Deletion:")}</strong> {t("Ask for your data to be removed (subject to legal constraints).")}
            </li>
          </ul>
          <p>
            {t("To exercise these rights, contact us at")}&nbsp;
            <a href="mailto:info@myomnamo.com" className="text-blue-500">
              info@myomnamo.com
            </a>
            .
          </p>

          <h4 className="text-xl font-semibold mt-6">{t("7. Third-Party Links")}</h4>
          <p>
            {t("Our website may contain links to third-party websites. We are not responsible for their privacy policies.")}
          </p>

          <h4 className="text-xl font-semibold mt-6">{t("8. Children’s Privacy")}</h4>
          <p>
            {t("Our services are for users above 18. If a child provides information, contact us for immediate removal.")}
          </p>

          <h4 className="text-xl font-semibold mt-6">
            {t("9. Changes to This Privacy Policy")}
          </h4>
          <p>
            {t("We may update this Privacy Policy. Changes will be posted on this page.")}
          </p>

          <h4 className="text-xl font-semibold mt-6">{t("10. Contact Us")}</h4>
          <p>{t("If you have any questions, contact us:")}</p>
          <p>
            <strong>{t("Email:")}</strong>{" "}
            <a href="mailto:info@myomnamo.com" className="text-blue-500">
              info@myomnamo.com
            </a>
          </p>
          <p>
            <strong>{t("Phone:")}</strong> +91 8879222277
          </p>

          <p className="mt-6">
            {t("By using Myomnamo, you acknowledge that you have read and understood this Privacy Policy.")}
          </p>

          <p className="font-semibold mt-6">
            {t("Thank you for trusting Myomnamo for your spiritual journey.")}
          </p>
        </div>
      </section>

    </>
  );
};

export default PrivacyPolicyPage;
